/**
 * Utility functions for recall data handling
 */

/**
 * Parsed batch information from RappelConso composite format
 */
export interface ParsedBatchInfo {
  gtin: string | null;
  lot: string;
  dateType: string | null;
  date: string | null;
}

/**
 * Parse product identification from RappelConso composite format
 * Format: GTIN$LOT$type_date$DATE$ (multiple entries separated by |)
 * 
 * Examples:
 * - "3288131590318$900932$date limite de consommation$2027-08-18$"
 * - "3288131590318$900932$dlc$2027-08-18$|3288131590319$900933$ddm$2027-09-01$"
 * - "tous les lots" (simple text)
 * 
 * @param rawBatchLot - Raw batch_lot string from database
 * @returns Array of parsed batch information
 */
export function parseProductIdentification(rawBatchLot: string | null | undefined): ParsedBatchInfo[] {
  if (!rawBatchLot) return [];
  
  const trimmed = rawBatchLot.trim();
  
  // Format 1: JSON array string (e.g. '["GTIN","LOT","date_type","date",""]')
  if (trimmed.startsWith('[')) {
    return parseJsonArrayFormat(trimmed);
  }
  
  // Format 2: Simple text without separators (e.g., "tous les lots")
  if (!trimmed.includes('$')) {
    return [{
      gtin: null,
      lot: trimmed,
      dateType: null,
      date: null
    }];
  }
  
  // Format 3: Dollar-separated (e.g. "GTIN$LOT$type$date$")
  const batches = trimmed.split('|').filter(Boolean);
  
  return batches.map(batch => {
    const parts = batch.split('$').filter(p => p !== '');
    const potentialGtin = parts[0];
    const isValidGtin = potentialGtin && /^\d{8,14}$/.test(potentialGtin);
    
    return {
      gtin: isValidGtin ? potentialGtin : null,
      lot: isValidGtin ? (parts[1] || parts[0]) : parts[0] || batch,
      dateType: parts[2] || null,
      date: parts[3] || null
    };
  });
}

/**
 * Parse JSON array format from database
 * e.g. '["3361114015067","980/605103201","date limite de consommation","2026-02-27",""]'
 * Multi-lot: '["GTIN1","LOT1","type","date","|","GTIN2","LOT2","type","date",""]'
 */
function parseJsonArrayFormat(raw: string): ParsedBatchInfo[] {
  let items: string[];
  try {
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [{ gtin: null, lot: raw, dateType: null, date: null }];
    items = parsed.map(String);
  } catch {
    return [{ gtin: null, lot: raw, dateType: null, date: null }];
  }
  
  // Split into batches by "|" separator
  const batches: string[][] = [];
  let current: string[] = [];
  
  for (const item of items) {
    if (item === '|') {
      if (current.length > 0) batches.push(current);
      current = [];
    } else {
      current.push(item);
    }
  }
  if (current.length > 0) batches.push(current);
  
  return batches.map(parts => {
    const meaningful = parts.filter(p => p.trim() !== '');
    
    const potentialGtin = meaningful[0] || '';
    const isValidGtin = /^\d{8,14}$/.test(potentialGtin) && !/^0+$/.test(potentialGtin);
    
    return {
      gtin: isValidGtin ? potentialGtin : null,
      lot: isValidGtin ? (meaningful[1] || potentialGtin) : (meaningful[0] || parts.join(' ')),
      dateType: meaningful[isValidGtin ? 2 : 1] || null,
      date: meaningful[isValidGtin ? 3 : 2] || null
    };
  });
}

/**
 * Format date type label in French
 */
export function formatDateTypeLabel(dateType: string | null): string {
  if (!dateType) return 'Date limite';
  
  const normalized = dateType.toLowerCase().trim();
  
  if (normalized.includes('consommation') || normalized === 'dlc') {
    return 'Date limite de consommation';
  }
  if (normalized.includes('durabilité') || normalized.includes('minimale') || normalized === 'ddm') {
    return 'Date de durabilité minimale';
  }
  
  return dateType;
}

/**
 * Risk categories for filtering - distinct from pathogens
 * Used for main feed filtering by risk family
 */
export const RISK_CATEGORIES = {
  pathogen: {
    label: 'Pathogènes',
    emoji: '🦠',
    types: [
      'listeria', 'salmonella', 'ecoli', 'toxoplasma',
      'staphylococcus', 'campylobacter', 'clostridium_botulinum',
      'bacillus_cereus', 'clostridium_perfringens',
      'norovirus', 'rotavirus', 'pathogene_autre'
    ]
  },
  chemical: {
    label: 'Chimiques',
    emoji: '🧪',
    types: [
      'mycotoxine_aflatoxine', 'mycotoxine_ochratoxine',
      'pesticide_eto', 'chimique_moah_pah'
    ]
  },
  allergen: {
    label: 'Allergènes',
    emoji: '⚠️',
    types: ['allergene']
  },
  packaging: {
    label: 'Emballage',
    emoji: '📦',
    types: ['conditionnement']
  },
  other: {
    label: 'Autre',
    emoji: '❓',
    types: ['risque_autre']
  }
} as const;

export type RiskCategoryKey = keyof typeof RISK_CATEGORIES;

/**
 * Get the risk category for a given risk_type
 */
export function getRiskCategory(riskType: string): RiskCategoryKey {
  for (const [category, data] of Object.entries(RISK_CATEGORIES)) {
    if ((data.types as readonly string[]).includes(riskType)) {
      return category as RiskCategoryKey;
    }
  }
  return 'other';
}

/**
 * Get the official source URL for a recall
 * CRITICAL: Always use source_url from the database, never generate URLs
 * 
 * @param recall - The recall object with source_url property
 * @returns The exact source URL or null if unavailable
 */
export function getOfficialSourceUrl(recall: { source_url?: string; official_url?: string }): string | null {
  // Priority 1: Use source_url from database (most reliable)
  if (recall.source_url && isValidUrl(recall.source_url)) {
    return recall.source_url;
  }
  
  // Priority 2: Fallback to official_url if available
  if (recall.official_url && isValidUrl(recall.official_url)) {
    return recall.official_url;
  }
  
  // No valid URL available
  return null;
}

/**
 * Validate if a string is a valid HTTP/HTTPS URL
 */
function isValidUrl(urlString: string): boolean {
  try {
    const url = new URL(urlString);
    return url.protocol === 'http:' || url.protocol === 'https:';
  } catch {
    return false;
  }
}

/**
 * Convert severity_score to severity level
 * DEPRECATED: Use getSeverityLevelByRiskType() for UI display
 * This function is only kept for push notification filtering logic
 * Mapping aligned with database scoring:
 * - high: score >= 4 (critical pathogens with bonuses)
 * - medium: score 2-3 (moderate pathogens, base scores)
 * - low: score 1 (packaging defects only)
 */
export function getSeverityLevel(severityScore: number): 'high' | 'medium' | 'low' {
  if (severityScore >= 4) return 'high';
  if (severityScore >= 2) return 'medium';
  return 'low';
}

/**
 * Get severity level based on risk_type for UI display
 * This provides consistent severity display based on pathogen nature
 * regardless of dynamic score bonuses (pregnancy, baby food, recency)
 * 
 * @param riskType - The risk_type enum value from database
 * @returns 'high' | 'medium' | 'low' for UI badge display
 */
export function getSeverityLevelByRiskType(riskType: string): 'high' | 'medium' | 'low' {
  // Critical pathogens (base score 3)
  const criticalPathogens = [
    'listeria',
    'salmonella', 
    'ecoli',
    'toxoplasma',
    'staphylococcus',
    'campylobacter',
    'clostridium_botulinum'
  ];
  
  // Moderate biological risks (base score 2)
  const moderateRisks = [
    'bacillus_cereus',
    'clostridium_perfringens',
    'norovirus',
    'rotavirus',
    'pathogene_autre',
    'mycotoxine_aflatoxine',
    'mycotoxine_ochratoxine',
    'pesticide_eto',
    'chimique_moah_pah',
    'risque_autre',
    'allergene'
  ];
  
  if (criticalPathogens.includes(riskType)) {
    return 'high';
  }
  
  if (moderateRisks.includes(riskType)) {
    return 'medium';
  }
  
  // Low severity (packaging defects, base score 1)
  return 'low';
}

/**
 * Get agent/pathogen display name in French
 */
export function getAgentDisplayName(riskType: string): string {
  const agentNames: Record<string, string> = {
    listeria: 'Listeria monocytogenes',
    salmonella: 'Salmonella',
    toxoplasma: 'Toxoplasma',
    ecoli: 'Escherichia coli (E. coli)',
    staphylococcus: 'Staphylococcus aureus',
    bacillus_cereus: 'Bacillus cereus',
    mycotoxine_aflatoxine: 'Aflatoxines',
    mycotoxine_ochratoxine: 'Ochratoxine A',
    pesticide_eto: 'Oxyde d\'éthylène',
    chimique_moah_pah: 'MOAH/PAH',
    conditionnement: 'Défaut d\'emballage',
    risque_autre: 'Risque non précisé',
    allergene: 'Allergène non déclaré'
  };
  
  return agentNames[riskType] || riskType;
}

/**
 * Display label (with emoji) for a recall's risk_type.
 * When risk_type is the generic `risque_autre`, inspect the motive
 * to surface a more informative sub-label (étiquetage / qualité)
 * without changing the underlying enum value.
 */
export function getRiskDisplayLabel(riskType: string, motive?: string): string {
  if (riskType === 'risque_autre' && motive) {
    const m = motive.toLowerCase();
    if (/[ée]tiquetage|\bddm\b|date\s+de\s+durabilit[ée]|date\s+limite|dlc\b/i.test(m)) {
      return "📋 Défaut d'étiquetage";
    }
    if (/organoleptique|aspect|couleur|odeur|go[uû]t|texture/i.test(m)) {
      return '🔬 Défaut qualité';
    }
  }

  const labels: Record<string, string> = {
    'listeria': '🦠 Listeria',
    'salmonella': '🦠 Salmonella',
    'ecoli': '🦠 E. coli',
    'toxoplasma': '🦠 Toxoplasma',
    'staphylococcus': '🦠 Staphylococcus aureus',
    'bacillus_cereus': '🦠 Bacillus cereus',
    'campylobacter': '🦠 Campylobacter',
    'clostridium_botulinum': '🦠 Botulisme',
    'clostridium_perfringens': '🦠 Clostridium perfringens',
    'norovirus': '🦠 Norovirus',
    'rotavirus': '🦠 Rotavirus',
    'pathogene_autre': '🦠 Autre pathogène',
    'mycotoxine_aflatoxine': '⚠️ Aflatoxines',
    'mycotoxine_ochratoxine': '⚠️ Ochratoxine A',
    'pesticide_eto': '🧪 Pesticides ETO',
    'chimique_moah_pah': '🧪 MOAH/PAH',
    'conditionnement': '📦 Emballage',
    'risque_autre': '❓ Risque non précisé',
    'allergene': '⚠️ Allergène non déclaré',
  };
  return labels[riskType] || riskType;
}

/**
 * Get relative time string in French
 */
export function getRelativeTimeString(dateString: string): string {
  const now = new Date();
  const date = new Date(dateString);
  const diffInHours = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60));
  
  if (diffInHours < 1) return 'À l\'instant';
  if (diffInHours < 24) return `Il y a ${diffInHours}h`;
  if (diffInHours < 48) return 'Hier';
  
  const diffInDays = Math.floor(diffInHours / 24);
  if (diffInDays < 7) return `Il y a ${diffInDays}j`;
  if (diffInDays < 30) return `Il y a ${Math.floor(diffInDays / 7)} sem.`;
  if (diffInDays < 365) return `Il y a ${Math.floor(diffInDays / 30)} mois`;
  
  return `Il y a ${Math.floor(diffInDays / 365)} an${diffInDays >= 730 ? 's' : ''}`;
}

/**
 * Format date for display
 */
export function formatDate(dateString: string): string {
  const date = new Date(dateString);
  return new Intl.DateTimeFormat('fr-FR', {
    day: 'numeric',
    month: 'long',
    year: 'numeric'
  }).format(date);
}

/**
 * Proxy image URL through Edge Function to bypass hotlinking restrictions
 */
export function getProxiedImageUrl(imageUrl: string): string {
  // Only proxy images from rappel.conso.gouv.fr (which blocks hotlinking)
  if (imageUrl.includes('rappel.conso.gouv.fr')) {
    return `https://cbjrjgphszrfytseltmp.supabase.co/functions/v1/proxy-image?url=${encodeURIComponent(imageUrl)}`;
  }
  return imageUrl;
}

/**
 * Small (160px) variant of a proxied image, for list cards and notifications.
 * Cuts storage egress ~10x versus the full image. Non-proxied URLs are unchanged.
 */
export function getThumbnailUrl(url: string): string {
  if (url.includes('/functions/v1/proxy-image?') && !url.includes('size=thumb')) {
    return `${url}&size=thumb`;
  }
  return url;
}

/**
 * Validate and filter valid image URLs from array or pipe/space-separated string
 * Handles malformed data where multiple URLs are concatenated with | or spaces
 * Returns proxied URLs for domains that block hotlinking
 */
export function getValidImageUrls(images: string[] | string | undefined): string[] {
  if (!images) return [];
  
  // Handle string or array input
  let rawImages: string[];
  if (typeof images === 'string') {
    rawImages = [images];
  } else if (Array.isArray(images)) {
    rawImages = images;
  } else {
    return [];
  }
  
  // Flatten array by splitting on | or spaces (handles malformed data)
  const flatImages: string[] = [];
  for (const img of rawImages) {
    if (!img || typeof img !== 'string') continue;
    // Split by | (V2 API separator) or whitespace
    const parts = img.split(/[|\s]+/).filter(Boolean);
    flatImages.push(...parts);
  }
  
  // Validate each URL and proxy if needed
  return flatImages
    .filter(img => {
      try {
        const url = new URL(img);
        return url.protocol === 'http:' || url.protocol === 'https:';
      } catch {
        return false;
      }
    })
    .map(getProxiedImageUrl);
}

/**
 * Returns the Tailwind border color class for a given severity level.
 * Uses the same semantic tokens as SeverityBadge for visual consistency.
 * 
 * Mapping (aligned with Badge variants):
 * - high → border-l-destructive (destructive variant)
 * - medium → border-l-primary (default variant)
 * - low → border-l-secondary (secondary variant)
 * 
 * @param severity - The severity level ('high' | 'medium' | 'low')
 * @returns Tailwind class string for left border color
 */
export function getSeverityBorderColor(severity: 'high' | 'medium' | 'low'): string {
  switch (severity) {
    case 'high':
      return 'border-l-destructive';
    case 'medium':
      return 'border-l-warning';
    case 'low':
      return 'border-l-secondary';
  }
}

/**
 * Une recherche est considérée comme un code-barres (EAN-8, UPC-A, EAN-13, GTIN-14)
 * lorsqu'elle ne contient que 8 à 14 chiffres (espaces ignorés côté appelant).
 */
export function isBarcodeQuery(query: string): boolean {
  return /^\d{8,14}$/.test(query.trim());
}
